import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Moon, Sun, Volume2, VolumeX, Menu, X } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings } from '../services/publicContentService';

const NAV_LINKS = [
  { to: '/', ar: 'من نحن', en: 'About' },
  { to: '/services', ar: 'خدماتنا', en: 'Services' },
  { to: '/accreditations', ar: 'مركز الاعتمادات', en: 'Accreditations' },
  { to: '/news', ar: 'الأخبار', en: 'News' },
  { to: '/contact', ar: 'تواصل معنا', en: 'Contact Us' },
];

const Header = ({ theme, toggleTheme, soundEnabled, toggleSound }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const isAr = language === 'ar';
  const location = useLocation();

  // Optional CMS-driven brand logos — falls back to the built-in mock URL.
  const [branding, setBranding] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('branding').then((v) => { if (alive) setBranding(v || {}); });
    return () => { alive = false; };
  }, []);
  const isDark = theme === 'dark';
  const cmsLogo = isDark
    ? ((branding.logo_dark || '').trim() || (branding.logo_light || '').trim())
    : ((branding.logo_light || '').trim() || (branding.logo_dark || '').trim());
  const logoSrc = cmsLogo || companyInfo.logo;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="absolute inset-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
        <div
          className={`absolute inset-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg transition-opacity duration-500 ${
            scrolled ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </div>

      <div className="relative container mx-auto px-4 lg:px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <NavLink to="/" className="flex items-center flex-shrink-0 order-1 group">
            <div className="relative">
              <div className="absolute inset-0 bg-brand-blue/15 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <img
                src={logoSrc}
                alt={companyInfo.name}
                style={{ minWidth: '170px' }}
                className="relative h-16 lg:h-20 w-auto max-w-none object-contain transition-all duration-500
                         group-hover:scale-105 drop-shadow-[0_0_12px_rgba(58,122,186,0.35)]
                         dark:invert dark:hue-rotate-180"
              />
            </div>
          </NavLink>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8 flex-1 justify-center order-2">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                data-testid={`nav-${link.to.replace('/', '') || 'home'}`}
                className={({ isActive }) =>
                  `relative text-base font-semibold tracking-wide transition-all duration-300
                   ${
                     isActive
                       ? 'text-brand-blue dark:text-brand-blue-300'
                       : 'text-slate-700 dark:text-slate-300 hover:text-brand-blue dark:hover:text-brand-blue-300'
                   }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative z-10">{isAr ? link.ar : link.en}</span>
                    {isActive && (
                      <span className="absolute -bottom-1.5 left-0 right-0 mx-auto w-8 h-0.5 bg-gradient-to-r from-brand-blue via-brand-green to-brand-orange rounded-full" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Controls */}
          <div className="flex items-center gap-2 flex-shrink-0 order-3">
            <button
              onClick={toggleLanguage}
              data-testid="lang-toggle"
              className="group hidden sm:flex p-2.5 rounded-lg bg-slate-100 dark:bg-slate-800
                       hover:bg-brand-blue-50 dark:hover:bg-slate-700 transition-all duration-300
                       border border-slate-200 dark:border-slate-700"
              title={isAr ? 'English' : 'عربي'}
            >
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                {isAr ? 'إنجليزي' : 'العربية'}
              </span>
            </button>

            {toggleSound && (
              <button
                onClick={toggleSound}
                data-testid="sound-toggle"
                className="relative group p-2.5 rounded-lg bg-slate-100 dark:bg-slate-800
                    hover:bg-brand-blue-50 dark:hover:bg-slate-700 transition-all duration-300
                    border border-slate-200 dark:border-slate-700"
              >
                {soundEnabled ? (
                  <>
                    <Volume2 className="w-5 h-5 text-brand-blue dark:text-brand-blue-300" />
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-brand-blue rounded-full animate-pulse" />
                  </>
                ) : (
                  <VolumeX className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                )}
              </button>
            )}

            {toggleTheme && (
              <button
                onClick={toggleTheme}
                data-testid="theme-toggle"
                className="group p-2.5 rounded-lg bg-slate-100 dark:bg-slate-800
                         hover:bg-brand-blue-50 dark:hover:bg-slate-700 transition-all duration-300
                         border border-slate-200 dark:border-slate-700"
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                ) : (
                  <Sun className="w-5 h-5 text-brand-orange" />
                )}
              </button>
            )}

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2.5 rounded-lg bg-slate-100 dark:bg-slate-800
                       hover:bg-brand-blue-50 dark:hover:bg-slate-700 transition-all duration-300
                       border border-slate-200 dark:border-slate-700"
              aria-label="Menu"
            >
              {mobileOpen ? (
                <X className="w-5 h-5 text-slate-700 dark:text-slate-300" />
              ) : (
                <Menu className="w-5 h-5 text-slate-700 dark:text-slate-300" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden mt-4 pb-4 animate-fade-in">
            <nav className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === '/'}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-brand-blue/10 text-brand-blue dark:text-brand-blue-300 border border-brand-blue/20'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`
                  }
                >
                  {isAr ? link.ar : link.en}
                </NavLink>
              ))}
              <button
                onClick={toggleLanguage}
                className="sm:hidden mt-2 px-4 py-3 rounded-lg text-sm font-medium
                         bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
              >
                {isAr ? 'English' : 'العربية'}
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-blue/40 to-transparent shadow-[0_0_15px_rgba(58,122,186,0.4)]" />
    </header>
  );
};

export default Header;
